import { Box, createStyles, Group, Stack, UnstyledButton } from '@mantine/core';
import type { ESQueryModel, QueryLiteSecodaEntity } from '@repo/api-codegen';
import RichTooltip from '@repo/common/components/RichTooltip/RichTooltip';
import type { EntityType } from '@repo/common/enums/entityType';
import { Text } from '@repo/foundations';
import IntegrationLogo from '../../../components/IntegrationLogo/IntegrationLogo';
import { entityUrl } from '../../../utils/navigationUtils';
import { QueryResourcePreview } from './QueryResourcePreview';

const PILL_PADDING = 10;
const PILL_ICON_WIDTH = 20;
const PILL_SPACING = 8;
const MORE_PILL_WIDTH = 84; // width calculated based on a three digit number: +xxx more
const RESOURCE_LINE_WIDTH = 492;

const calculatePillWidth = (
	context: CanvasRenderingContext2D,
	title: string
) => {
	const textWidth = context.measureText(title).width;
	return PILL_PADDING + PILL_ICON_WIDTH + textWidth;
};

const getDisplayableResources = (entities: QueryLiteSecodaEntity[]) => {
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');

	if (!context) return { displayableResources: entities, extraEntities: 0 };

	context.font = '400 13px Inter';

	let remainingWidth = RESOURCE_LINE_WIDTH;
	let firstLineFull = false;
	const displayableResources: QueryLiteSecodaEntity[] = [];
	const extraResources: QueryLiteSecodaEntity[] = [];

	for (const entity of entities) {
		const pillWidth = calculatePillWidth(context, entity.title ?? '');
		const componentWidth = pillWidth + PILL_SPACING;

		if (remainingWidth - componentWidth >= 0) {
			displayableResources.push(entity);
			remainingWidth -= componentWidth;
		} else if (!firstLineFull) {
			firstLineFull = true;
			remainingWidth = RESOURCE_LINE_WIDTH - MORE_PILL_WIDTH;
			if (remainingWidth - componentWidth >= 0) {
				displayableResources.push(entity);
				remainingWidth -= componentWidth;
			} else {
				extraResources.push(entity);
			}
		} else {
			extraResources.push(entity);
		}
	}

	return { displayableResources, extraResources };
};

const useStyles = createStyles((theme) => ({
	icon: {
		alignSelf: 'center',
		paddingTop: 2,
		width: 20,
		height: 20,
	},
	pill: {
		background: theme.other.getColor('fill/transparent-secondary/default'),
		borderRadius: theme.radius.sm,
		padding: `0 ${theme.spacing['2xs']} 0 ${theme.spacing['4xs']}`,
		maxWidth: '260px',
		margin: `${theme.spacing['4xs']} 0`,
	},
	text: {
		fontSize: theme.fontSizes.sm,
		lineHeight: `20px`,
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
	},
	tooltipContainer: {
		maxHeight: '480px',
		maxWidth: '240px',
		overflowY: 'auto',
	},
	tooltipItem: {
		padding: theme.spacing['2xs'],
		borderRadius: theme.radius.sm,

		'&:hover': {
			backgroundColor: theme.other.getColor('fill/transparent-secondary/hover'),
		},
	},
}));

export function QueryResources({ query }: { query: ESQueryModel }) {
	const { classes, theme } = useStyles();

	const { displayableResources, extraResources } = getDisplayableResources(
		query.related_entities ?? []
	);

	const tooptipBody = (
		<Stack spacing={0} className={classes.tooltipContainer}>
			{extraResources?.map((entity) => (
				<UnstyledButton
					component="a"
					key={entity.id}
					href={entityUrl({
						id: entity.id,
						entity_type: entity.entity_type as EntityType,
					})}
				>
					<Group spacing="xs" className={classes.tooltipItem}>
						<IntegrationLogo
							integrationId={entity.integration_id ?? undefined}
							width={theme.other.space[4]}
							height={theme.other.space[4]}
						/>
						<Stack spacing={0}>
							<Text
								size="sm"
								lineClamp={1}
								data-testid="search-result-item"
								style={{
									maxWidth: '190px',
								}}
							>
								{entity.title || 'Untitled'}
							</Text>
							<Text size="xs" color="text/secondary/default" lineClamp={1}>
								{entity.entity_type}
							</Text>
						</Stack>
					</Group>
				</UnstyledButton>
			))}
		</Stack>
	);

	return (
		<Group spacing="xs" align="flex-start">
			{(displayableResources ?? []).map((resource) => (
				<QueryResourcePreview key={resource.id} query={resource}>
					<Box className={classes.pill}>
						<UnstyledButton
							component="a"
							href={entityUrl({
								id: resource.id,
								entity_type: resource.entity_type as EntityType,
							})}
						>
							<Group spacing="4xs" align="baseline">
								<Box className={classes.icon}>
									<IntegrationLogo
										integrationId={resource.integration_id ?? undefined}
										width={theme.other.space[4]}
										height={theme.other.space[4]}
									/>
								</Box>
								<Text className={classes.text}>{resource.title}</Text>
							</Group>
						</UnstyledButton>
					</Box>
				</QueryResourcePreview>
			))}
			{extraResources && extraResources.length > 0 && (
				<RichTooltip body={tooptipBody}>
					<Box className={classes.pill}>
						<UnstyledButton component="a">
							<Group spacing="4xs" align="baseline">
								<Text className={classes.text} color="text/secondary/default">
									+{extraResources.length} more
								</Text>
							</Group>
						</UnstyledButton>
					</Box>
				</RichTooltip>
			)}
		</Group>
	);
}
